/**
 * statMarkerUtils.ts
 * Centralized helpers for the new stat marker system.
 * All Mini chart components consume these instead of the old normalizeStatMarker().
 */

import { escapeHtml } from "src/pages/Insights/transformer";
import { IStatMarker, IStatMarkerCIRow, IStatMarkerItem, IStatTestConfig, IStatTestCIConfig } from "./../../../../../models/interfaces";

// ============================================================
// 0. MARKER LABEL → SYMBOL MAP
// ============================================================

export const STAT_MARKER_SYMBOL_MAP: Record<string, string> = {
	ARROW: "↑",
	TRIANGLE: "▲",
	HASH: "#",
	STAR: "*",
	CARET: "^",
};

/**
 * Resolves a marker_label string (e.g. "ARROW") to its display symbol (e.g. "↑").
 * Falls back to the original label if not found in the map.
 */
export const resolveMarkerSymbol = (markerLabel: string): string => {
	return STAT_MARKER_SYMBOL_MAP[markerLabel?.toUpperCase()] ?? markerLabel;
};

/**
 * Replaces all known marker label tokens (e.g. "ARROW", "TRIANGLE")
 * in a string with their display symbols (e.g. "↑", "▲").
 * Case-insensitive match on whole-word boundaries only.
 */
// Safe: built once from a static, code-defined key set (not user input);
// a simple word-boundary alternation with no backtracking risk.
const STAT_MARKER_TOKEN_REGEX = new RegExp(`\\b(${Object.keys(STAT_MARKER_SYMBOL_MAP).join("|")})\\b`, "gi");

export const resolveMarkerSymbolsInText = (text: string): string => {
	if (!text) return text;

	return text.replace(STAT_MARKER_TOKEN_REGEX, (match) => {
		return STAT_MARKER_SYMBOL_MAP[match.toUpperCase()] ?? match;
	});
};

// ============================================================
// 1. GUARD / CHECK HELPERS
// ============================================================

/** Does this series item have any renderable stat markers? */
export const hasStatMarkers = (statMarker: IStatMarker | null | undefined): boolean => {
	if (!statMarker?.enabled) return false;
	return statMarker.ci_rows.some((row) => row.items.length > 0);
};

/** Does ANY series item in an array have renderable markers? */
export const hasAnyStatMarkers = (series: { stat_marker?: IStatMarker | null }[], statTestConfig?: IStatTestConfig | null): boolean => {
	if (!statTestConfig?.enabled) return false;
	return series.some((s) => hasStatMarkers(s.stat_marker));
};

// ============================================================
// 2. DISPLAY TEXT COMPUTATION
// ============================================================

/** Compute display text for a single CI row: "↑^*" */
export const getCIRowDisplayText = (ciRow: IStatMarkerCIRow): string => {
	return ciRow.items.map((item) => resolveMarkerSymbol(item.marker_label)).join(",");
};

/** Get all renderable CI rows (those with items) paired with their config */
export interface IResolvedCIRow {
	ciLevel: string;
	displayText: string;
	items: IStatMarkerItem[];
	style: IStatTestCIConfig;
}

/**
 * Pre-check all resolved rows and determine if ALL would render as truncated/hidden.
 * If so, return true — callers should render a single combined truncated string.
 */
export const shouldCollapseToCombined = (resolvedRows: IResolvedCIRow[], availableWidth: number): boolean => {
	if (resolvedRows.length <= 1) return false;

	const MARKER_FONT_SIZE = 8;
	return resolvedRows.every((row) => {
		const mode = getMarkerRenderMode(row.displayText, availableWidth, MARKER_FONT_SIZE);
		return mode === MarkerRenderModeType.Truncated || mode === MarkerRenderModeType.Hidden;
	});
};

export const resolveStatMarkerRows = (statMarker: IStatMarker | null | undefined, statTestConfig: IStatTestConfig): IResolvedCIRow[] => {
	if (!statMarker?.enabled || !statTestConfig?.enabled) {
		return [];
	}

	const rows: IResolvedCIRow[] = [];

	for (const ciRow of statMarker.ci_rows) {
		if (ciRow.items.length === 0) {
			continue;
		}

		const style = statTestConfig.confidence?.[ciRow.ci_level];

		if (!style) {
			continue;
		}

		const resolvedRow: IResolvedCIRow = {
			ciLevel: ciRow.ci_level,
			displayText: getCIRowDisplayText(ciRow),
			items: ciRow.items,
			style: {
				...style,
				font_size: Math.min(style.font_size ?? MARKER_FONT_SIZE, MARKER_FONT_SIZE),
			},
		};
		rows.push(resolvedRow);
	}

	return rows;
};

// ============================================================
// 3. TRUNCATION / FALLBACK
// ============================================================

export enum MarkerRenderModeType {
	Full = "full",
	Truncated = "truncated",
	Icon = "icon",
	Hidden = "hidden",
	StrongTruncate = "strong_truncate",
}

const STAT_MARKER_ICON = "ⓘ";
const STAT_MARKER_ELLIPSIS = "..";
const DOT_WIDTH_MULTIPLIER = 0.35;

/** Get char width multiplier based on font size — smaller fonts render narrower */
export const getCharWidthMultiplier = (fontSize: number): number => {
	if (fontSize <= 7) return 0.52;
	if (fontSize <= 9) return 0.55;
	return 0.65;
};

/**
 * Determine render mode for a marker row given available pixel width.
 * charWidth is approximate px per character at the given font size.
 */
export const getMarkerRenderMode = (
	displayText: string,
	availableWidth: number,
	fontSize: number,
	strongTruncate: boolean = false,
	alwaysIconMode: boolean = false,
): MarkerRenderModeType => {

	if (availableWidth <= 0) {
		return MarkerRenderModeType.Hidden;
	}

	const charWidth = fontSize * getCharWidthMultiplier(fontSize);
	const fullWidth = displayText.length * charWidth;

	if (fullWidth <= availableWidth) {
		return MarkerRenderModeType.Full;
	}

	if (strongTruncate) {
		return MarkerRenderModeType.StrongTruncate;
	}

	const ellipsisWidth = STAT_MARKER_ELLIPSIS.length * DOT_WIDTH_MULTIPLIER * fontSize;
	const charsAfterEllipsis = Math.floor((availableWidth - ellipsisWidth) / charWidth);

	if (charsAfterEllipsis >= 1) {
		return MarkerRenderModeType.Truncated;
	}

	return MarkerRenderModeType.Hidden;
};

export const getRenderedMarkerText = (
	displayText: string,
	availableWidth: number,
	fontSize: number,
	strongTruncate: boolean = false,
	alwaysIconMode: boolean = false,
): { text: string; width: number; mode: MarkerRenderModeType; fontSize?: number } => {
	const mode = getMarkerRenderMode(displayText, availableWidth, fontSize, strongTruncate, alwaysIconMode);
	const charWidth = fontSize * getCharWidthMultiplier(fontSize);

	if (alwaysIconMode) {
		return { text: STAT_MARKER_ICON, width: 12, mode: MarkerRenderModeType.Icon, fontSize: 10 };
	}

	switch (mode) {
		case MarkerRenderModeType.Full:
			return { text: displayText, width: displayText.length * charWidth, mode };
		case MarkerRenderModeType.Truncated: {
			const ellipsis = STAT_MARKER_ELLIPSIS;
			const ellipsisWidth = ellipsis.length * DOT_WIDTH_MULTIPLIER * fontSize;
			const availableForText = availableWidth - ellipsisWidth;
			const maxChars = Math.max(1, Math.floor(availableForText / charWidth));
			const clipped = displayText.substring(0, maxChars) + ellipsis;
			return { text: clipped, width: maxChars * charWidth + ellipsisWidth, mode };
		}
		case MarkerRenderModeType.StrongTruncate: {
			const maxChars = Math.min(4, Math.floor(availableWidth / charWidth));
			const truncated = displayText.substring(0, maxChars);
			return { text: truncated, width: truncated.length * charWidth, mode };
		}
		case MarkerRenderModeType.Hidden:
		default:
			return { text: "", width: 0, mode };
	}
};

export const truncateDisplayText = (
	displayText: string,
	availableWidth: number,
	fontSize: number,
): string => {
	const charWidth = fontSize * getCharWidthMultiplier(fontSize);
	const ellipsis = STAT_MARKER_ELLIPSIS;
	const ellipsisWidth = ellipsis.length * DOT_WIDTH_MULTIPLIER * fontSize;
	const availableForText = availableWidth - ellipsisWidth;
	const maxChars = Math.floor(availableForText / charWidth);

	if (maxChars <= 0) {
		return ellipsis;
	}

	// Full text fits without truncation — no ellipsis needed
	if (displayText.length <= maxChars) {
		return displayText;
	}

	const truncated = displayText.substring(0, maxChars);

	// Try clean break at last comma boundary
	const lastComma = truncated.lastIndexOf(",");
	if (lastComma > 0) {
		return truncated.substring(0, lastComma) + ellipsis;
	}

	// No comma boundary — hard clip with ellipsis
	return truncated + ellipsis;
};

// ============================================================
// 4. TOOLTIP BUILDERS
// ============================================================

/**
 * Build HTML tooltip content for a stat marker hover.
 * Shows per-CI grouped items with optional p-values.
 */
export const buildStatMarkerTooltip = (seriesName: string, seriesColor: string, resolvedRows: IResolvedCIRow[], showPValue: boolean): string => {
	if (resolvedRows.length === 0) return "";

	const rowsHtml = resolvedRows
		.map((row) => {
			const itemsHtml = row.items
				.map((item) => {
					const symbol = escapeHtml(resolveMarkerSymbol(item.marker_label));
					const pValueStr = showPValue && item.p_value != null ? ` <span style="color:#9CA3AF;">(p=${escapeHtml(item.p_value.toFixed(4))})</span>` : "";
					const directionIcon = item.direction === "higher" ? "↑" : item.direction === "lower" ? "↓" : "";
					return `<span style="color:${escapeHtml(row.style.font_color)}; font-weight:${row.style.font_weight};">${symbol}</span>${directionIcon}${pValueStr}`;
				})
				.join(",");

			return `
				<div style="margin-bottom: 4px;">
					<div style="font-size: 9px; color: #9CA3AF; margin-bottom: 1px;">
						${escapeHtml(row.ciLevel)}% CI
					</div>
					<div style="font-size: 10px; color: #1F2937;">
						${itemsHtml}
					</div>
				</div>`;
		})
		.join("");

	return `
		<div style="padding: 4px; border-radius: 6px; font-family: inherit; min-width: 160px;">
			<div style="display: flex; align-items: center; gap: 6px; margin-bottom: 6px;">
				<span style="
					width: 8px; height: 8px;
					border-radius: 50%;
					background: ${escapeHtml(seriesColor)};
					display: inline-block;
				"></span>
				<span style="font-size: 10px; font-weight: 600; color: #374151;">${escapeHtml(seriesName)}</span>
			</div>
			${rowsHtml}
		</div>`;
};

// ============================================================
// 5. ECHARTS MARK-POINT BUILDERS (per chart type)
// ============================================================

/**
 * ECharts renders label backgrounds as SVG <path> with `paint-order: stroke`.
 * Visually, stroke-width: N renders as ~N/2 visible border.
 * Net result: pass the exact value you want as visible border width.
 */
const buildLabelBackgroundStyle = (style: IStatTestCIConfig) => {
	if (!style.background_enabled) return {};

	return {
		backgroundColor: style.background_color || "transparent",
		borderColor: style.border_color || "transparent",
		borderWidth: Math.max(0.5, style.border_width / 2),
		strokeWidth: 0.5,
		borderRadius: 2,
		padding: [1, 1, 1, 1],
	};
};

/** Default marker font size used across all chart types */
const MARKER_FONT_SIZE = 7;

/**
 * Build ECharts markPoint config for a HORIZONTAL CLUSTERED bar.
 * Markers render to the RIGHT of the data label.
 * All CI rows are combined into a single truncated string.
 */
export const buildHClusteredMarkPoints = (resolvedRows: IResolvedCIRow[], statTestConfig: IStatTestConfig, dataLabelWidth: number, availableWidth: number = Infinity): any[] => {
	if (resolvedRows.length === 0) return [];

	const GAP = 0;
	const cumulativeOffset = dataLabelWidth + GAP;
	const remainingWidth = Math.max(0, availableWidth - cumulativeOffset);

	if (remainingWidth <= 0) return [];

	// Combine all CI row display texts into a single string
	const combinedText = resolvedRows.map((row) => row.displayText).join(",");

	const baseStyle = resolvedRows[0].style;
	const rendered = getRenderedMarkerText(combinedText, remainingWidth, Math.min(baseStyle?.font_size || MARKER_FONT_SIZE, MARKER_FONT_SIZE));

	if (rendered.mode === MarkerRenderModeType.Hidden) return [];

	return [
		{
			type: "max" as const,
			label: {
				show: true,
				formatter: () => rendered.text,
				fontSize: Math.min(baseStyle?.font_size || MARKER_FONT_SIZE, MARKER_FONT_SIZE),
				letterSpacing: 0,
				fontWeight: baseStyle.font_weight,
				fontFamily: "inherit",
				color: baseStyle.font_color,
				position: "right" as const,
				distance: cumulativeOffset,
				align: "left" as const,
				verticalAlign: "middle" as const,
				...buildLabelBackgroundStyle(baseStyle),
			},
		},
	];
};

/**
 * Build ECharts markPoint config for a VERTICAL CLUSTERED bar.
 * Markers render ABOVE the bar, centered horizontally on top of the data label.
 * All CI rows are combined into a single truncated string.
 */
export const buildVClusteredMarkPoints = (resolvedRows: IResolvedCIRow[], statTestConfig: IStatTestConfig, dataLabelFontSize: number, barWidth: number): any[] => {
	if (resolvedRows.length === 0 || barWidth <= 0) return [];

	// Truncation based on actual bar width
	const availableWidth = barWidth;

	const combinedText = resolvedRows.map((row) => row.displayText).join(",");

	const baseStyle = resolvedRows[0].style;
	const rendered = getRenderedMarkerText(combinedText, availableWidth, Math.min(baseStyle?.font_size || MARKER_FONT_SIZE, MARKER_FONT_SIZE));

	if (rendered.mode === MarkerRenderModeType.Hidden) return [];

	// Distance above bar top: data label height + gap + marker half-height
	const dataLabelHeight = dataLabelFontSize + 2;
	const GAP = 2;
	const markerDistance = dataLabelHeight + GAP + MARKER_FONT_SIZE;

	return [
		{
			type: "max" as const,
			label: {
				show: true,
				formatter: () => rendered.text,
				fontSize: Math.min(baseStyle?.font_size || MARKER_FONT_SIZE, MARKER_FONT_SIZE),
				letterSpacing: 0,
				fontWeight: baseStyle.font_weight,
				fontFamily: "inherit",
				color: baseStyle.font_color,
				position: "top" as const,
				distance: markerDistance,
				offset: [0, 0],
				align: "center" as const,
				verticalAlign: "middle" as const,
				...buildLabelBackgroundStyle(baseStyle),
			},
		},
	];
};

/**
 * Build ECharts markPoint config for a HORIZONTAL STACKED bar.
 * Markers render inside the segment, above the data label.
 * All CI rows are combined into a single truncated string.
 */
export const buildHStackedMarkPoints = (
	resolvedRows: IResolvedCIRow[],
	statTestConfig: IStatTestConfig,
	segmentMidX: number,
	labelFontSize: number,
	segmentPixelWidth: number = Infinity,
): any[] => {
	if (resolvedRows.length === 0 || segmentPixelWidth <= 0) return [];

	// Use segment width with some padding for the marker text
	const SEGMENT_PADDING = 4;
	const availableWidth = Math.max(0, segmentPixelWidth - SEGMENT_PADDING);

	if (availableWidth <= 0) return [];

	const combinedText = resolvedRows.map((row) => row.displayText).join(",");

	const rendered = getRenderedMarkerText(combinedText, availableWidth, MARKER_FONT_SIZE);

	if (rendered.mode === MarkerRenderModeType.Hidden) return [];

	const baseStyle = resolvedRows[0].style;

	// Position marker above the data label inside the segment
	const baseOffsetY = -(Math.ceil(labelFontSize / 2) + 4);

	return [
		{
			coord: [segmentMidX, "Attr"],
			symbolSize: 0,
			label: {
				show: true,
				formatter: () => rendered.text,
				fontSize: Math.min(baseStyle?.font_size || MARKER_FONT_SIZE, MARKER_FONT_SIZE),
				letterSpacing: 0,
				fontWeight: baseStyle.font_weight,
				fontFamily: "inherit",
				color: baseStyle.font_color,
				position: "inside" as const,
				offset: [0, baseOffsetY],
				align: "center" as const,
				verticalAlign: "middle" as const,
				overflow: "truncate" as const,
				ellipsis: "..",
				width: Math.floor(segmentPixelWidth - SEGMENT_PADDING),
				...buildLabelBackgroundStyle(baseStyle),
			},
		},
	];
};

/**
 * Build ECharts markPoint config for a VERTICAL STACKED bar.
 * Tier 1: Full/truncated marker text rendered above the data label inside the segment.
 * Tier 2: Asterisk fallback if full text doesn't fit.
 * Tier 3: null — hide both label and marker (tooltip still works via series data).
 *
 * Returns { markPointData, hiddenLabel } where hiddenLabel=true means
 * the caller should suppress the data label for this segment.
 */
export const buildVStackedMarkPoints = (
	resolvedRows: IResolvedCIRow[],
	statTestConfig: IStatTestConfig,
	segmentMidY: number,
	labelHalfWidth: number,
	containerWidth: number = 0,
	barWidth: number = 0,
	segmentPixelHeight: number = 0,
	labelFontSize: number = 10,
): { markPointData: any[]; hideLabel: boolean } => {
	if (resolvedRows.length === 0) return { markPointData: [], hideLabel: false };

	const baseStyle = resolvedRows[0].style;
	const combinedText = resolvedRows.map((row) => row.displayText).join(",");

	const INSET_PADDING = 2;
	const availableWidth = Math.max(0, barWidth - INSET_PADDING);

	// Minimum height to render marker above label: label row + gap + marker row + gap
	const GAP = 2;
	const LABEL_HEIGHT = labelFontSize + GAP; // 12px
	const MIN_SEGMENT_FOR_MARKER = LABEL_HEIGHT + MARKER_FONT_SIZE + GAP * 2; // 23px
	const MIN_SEGMENT_FOR_ASTERISK = labelFontSize + GAP + MARKER_FONT_SIZE; // 19px

	const buildPoint = (text: string): any => {
		// Clamp offset so marker stays within segment bounds
		// Max safe offset = half segment height - half marker font size
		const maxOffset = Math.floor(segmentPixelHeight / 2) - Math.ceil(MARKER_FONT_SIZE / 2);
		const desiredOffset = LABEL_HEIGHT;
		const clampedOffset = Math.min(desiredOffset, Math.max(0, maxOffset));

		return {
			coord: ["Wave", segmentMidY],
			symbolSize: 0,
			label: {
				show: true,
				formatter: () => text,
				letterSpacing: 0,
				fontSize: Math.min(baseStyle?.font_size || MARKER_FONT_SIZE, MARKER_FONT_SIZE),
				fontWeight: baseStyle.font_weight,
				fontFamily: "inherit",
				color: baseStyle.font_color,
				position: "inside" as const,
				offset: [0, -(clampedOffset)],
				align: "center" as const,
				verticalAlign: "middle" as const,
				...buildLabelBackgroundStyle(baseStyle),
			},
		};
	};

	// --- Tier 1: segment tall enough for both label + marker text ---
	if (segmentPixelHeight >= MIN_SEGMENT_FOR_MARKER && availableWidth > 0) {
		const rendered = getRenderedMarkerText(combinedText, availableWidth, Math.min(baseStyle?.font_size, MARKER_FONT_SIZE));
		if (rendered.mode !== MarkerRenderModeType.Hidden && rendered.text) {
			return { markPointData: [buildPoint(rendered.text)], hideLabel: false };
		}
	}

	// --- Tier 3: nothing fits — hide label, no marker rendered, tooltip still works ---
	return { markPointData: [], hideLabel: true };
};

/**
 * Build ECharts markPoint config for a HORIZONTAL LINE chart.
 * Markers render top-right of the data point label, chained left-to-right.
 * All CI rows are combined into a single truncated string.
 */
export const buildHLineMarkPoints = (
	resolvedRows: IResolvedCIRow[],
	statTestConfig: IStatTestConfig,
	attribute: string,
	value: number,
	dataLabelHalfWidth: number,
	availableWidth: number = Infinity,
): any[] => {
	if (resolvedRows.length === 0) return [];

	const cumulativeX = dataLabelHalfWidth + 1;
	const remainingWidth = Math.max(0, availableWidth - cumulativeX);

	if (remainingWidth <= 0) return [];

	const combinedText = resolvedRows.map((row) => row.displayText).join(",");

	const baseStyle = resolvedRows[0].style;

	const rendered = getRenderedMarkerText(combinedText, availableWidth, Math.min(baseStyle?.font_size || MARKER_FONT_SIZE));

	if (rendered.mode === MarkerRenderModeType.Hidden) return [];

	const badgePadding = baseStyle.background_enabled ? 6 : 0;
	const badgeWidth = rendered.width + badgePadding;
	const xOffset = cumulativeX + badgeWidth / 2;

	return [
		{
			coord: [attribute, value],
			symbolSize: 0,
			label: {
				show: true,
				formatter: () => rendered.text,
				fontSize: Math.min(baseStyle?.font_size || MARKER_FONT_SIZE, MARKER_FONT_SIZE),
				letterSpacing: 0,
				fontWeight: baseStyle.font_weight,
				fontFamily: "inherit",
				color: baseStyle.font_color,
				position: "top" as const,
				offset: [xOffset, -9],
				align: "center" as const,
				verticalAlign: "middle" as const,
				...buildLabelBackgroundStyle(baseStyle),
			},
		},
	];
};

/**
 * Build ECharts markPoint config for a VERTICAL LINE chart.
 * Markers render below-right of the data point label.
 * All CI rows are combined into a single truncated string.
 */
export const buildVLineMarkPoints = (
	resolvedRows: IResolvedCIRow[],
	statTestConfig: IStatTestConfig,
	value: number,
	attribute: string,
	dataLabelHalfHeight: number,
	availableWidth: number = Infinity,
): any[] => {
	if (resolvedRows.length === 0) return [];

	if (availableWidth <= 0) return [];

	const combinedText = resolvedRows.map((row) => row.displayText).join(",");
	const baseStyle = resolvedRows[0].style;

	const rendered = getRenderedMarkerText(combinedText, availableWidth, Math.min(baseStyle?.font_size || MARKER_FONT_SIZE), true);

	if (rendered.mode === MarkerRenderModeType.Hidden) return [];

	return [
		{
			coord: [value, attribute],
			symbolSize: 0,
			label: {
				show: true,
				formatter: () => rendered.text,
				fontSize: Math.min(baseStyle?.font_size || MARKER_FONT_SIZE, MARKER_FONT_SIZE),
				letterSpacing: 0,
				fontWeight: baseStyle.font_weight,
				fontFamily: "inherit",
				color: baseStyle.font_color,
				position: "right" as const,
				offset: [12, -(dataLabelHalfHeight + 6)],
				align: "center" as const,
				verticalAlign: "middle" as const,
				...buildLabelBackgroundStyle(baseStyle),
			},
		},
	];
};

// ============================================================
// 6. BACKWARD COMPAT — old normalizeStatMarker replacement
// ============================================================

/**
 * @deprecated Use resolveStatMarkerRows() instead.
 * Kept temporarily for any code that still passes the old string/string[] format.
 */
export const normalizeStatMarkerLegacy = (marker?: string | string[]): string => {
	if (!marker) return "";
	if (Array.isArray(marker)) return marker.filter(Boolean).join(",");
	return marker;
};
